import * as React from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Toaster } from "../components/ui/toaster";
import { useToast } from "../components/ui/use-toast";
import { useEffect, useMemo, useState } from "react";
import { Navbar } from "../components/Navbar";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "../components/ui/pagination";
import {
  useCreateOrganizationMutation,
  useGetOrganizationsQuery,
} from "../generated/graphql";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../components/ui/sheet";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { CircleDot } from "lucide-react";
import { CheckCircle } from "lucide-react";

export function Organizations() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [name, setName] = useState("");

  const [page, setPage] = React.useState(1);
  const [search, setSearch] = useState<string>("");
  const [
    { data: organizationsData, error: organizationsError },
    refetchOrganizations,
  ] = useGetOrganizationsQuery({
    variables: { page },
  });

  const [
    { data: createOrganizationData, error: createOrganizationError },
    createOrganization,
  ] = useCreateOrganizationMutation();

  useEffect(() => {
    if (createOrganizationData) {
      setName("");
      toast({
        title: "Success",
        description: "Organization created successfully",
        variant: "default",
      });
      refetchOrganizations({ requestPolicy: "network-only" });
    }
  }, [createOrganizationData, toast, refetchOrganizations]);

  useEffect(() => {
    if (createOrganizationError) {
      toast({
        title: "Error",
        description: createOrganizationError.message,
        variant: "destructive",
      });
    }

    if (organizationsError) {
      toast({
        title: "Error",
        description: organizationsError.message,
        variant: "destructive",
      });
    }
  }, [createOrganizationError, toast, organizationsError]);

  const organizations = organizationsData?.getOrganizations;

  const memoizedOrganizations = useMemo(() => {
    if (search !== "") {
      return organizations
        ?.filter((organization) =>
          organization.name.toLowerCase().includes(search.toLowerCase())
        )
        .sort((a, b) => {
          // Convert names to lowercase for case-insensitive sorting
          const nameA = a.name.toLowerCase();
          const nameB = b.name.toLowerCase();

          if (nameA < nameB) return -1;
          if (nameA > nameB) return 1;
          return 0;
        });
    }
    return organizations;
  }, [organizations, search]);

  return (
    <div className="flex min-h-screen w-full flex-col">
      <Navbar />
      <main className="flex flex-1 flex-col gap-4 p-4 md:gap-8 md:p-8">
        <Card className="xl:col-span-2">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="grid gap-2">
              <CardTitle>Organizations</CardTitle>
            </div>
            <div className="flex flex-row gap-2">
              <Input
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className="bg-black text-white "
                  style={{
                    marginTop: "0px",
                  }}
                >
                  New
                </Button>
              </SheetTrigger>
              <SheetContent side="bottom">
                <SheetHeader>
                  <SheetTitle>Add Organization</SheetTitle>
                </SheetHeader>
                <div className="grid gap-4 py-4">
                  <div className="grid grid-cols-4 items-center gap-4">
                    <Label htmlFor="name" className="text-right">
                      Name
                    </Label>
                    <Input
                      id="name"
                      placeholder="name"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                      }}
                      className="col-span-3"
                    />
                  </div>
                </div>
                <SheetFooter>
                  <SheetClose asChild>
                    <Button
                      type="submit"
                      onClick={async () => {
                        await createOrganization({
                          name: name,
                        });

                        refetchOrganizations({
                          requestPolicy: "network-only",
                        });
                      }}
                    >
                      Create Organization
                    </Button>
                  </SheetClose>
                </SheetFooter>
              </SheetContent>
            </Sheet>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead
                    style={{
                      width: "5px",
                    }}
                  />
                  <TableHead>Name</TableHead>
                  <TableHead>ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {memoizedOrganizations?.map((organization, idx) => (
                  <TableRow
                    key={idx}
                    onClick={async () => {
                      navigate(`/organizations/${organization.id}`);
                    }}
                  >
                    <TableCell>
                      {organization.hasPendingTopups ? (
                        <CircleDot className="h-4 w-4 text-red-500" />
                      ) : null}
                      {organization.autoApprove ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : null}
                      {organization.hasBalance ? (
                        <CheckCircle className="h-4 w-4 text-yellow-500" />
                      ) : null}
                    </TableCell>
                    <TableCell>{organization.name}</TableCell>
                    <TableCell>{organization.id?.slice(0, 6)}...</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    onClick={() => {
                      setPage(page - 1);
                    }}
                  />
                </PaginationItem>
                <PaginationItem>
                  <PaginationLink>{page}</PaginationLink>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    onClick={() => {
                      setPage(page + 1);
                    }}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </CardContent>
        </Card>
      </main>
      <Toaster />
    </div>
  );
}
